/**
 * EngagementsUtil - Engagement partitioning, sorting, filtering, and badge configuration
 * ONLY concerned with engagement data transformation - no API calls, no UI rendering, no state management
 */
import type {
  CandidateEngagement,
  CandidateEngagementDetails,
  OpportunityCandidate,
} from '@axiom/validation';
import {
  CandidateEngagementsConst,
  CandidateOpportunitiesConst,
} from '@axiom/const';
import { DateUtil, PositionUtil } from '@axiom/ui';
import type { Badge } from '@axiom/const';
import type { BadgeBackgroundType } from '@axiom/ui';

const { Badges, EngagementStates } = CandidateEngagementsConst;
const { CandidateStatuses } = CandidateOpportunitiesConst;

const CurrentOpportunityPartitionStatuses = new Set([
  CandidateStatuses.Interested,
  CandidateStatuses.Warmed,
  CandidateStatuses.WarmedYes,
]);
const CurrentSubmissionPartitionStatuses = new Set([
  CandidateStatuses.Interviewing,
  CandidateStatuses.Selected,
  CandidateStatuses.Submitted,
  CandidateStatuses.Rejected,
]);

export type BadgeConfig = {
  badge: Badge;
  color: BadgeBackgroundType;
  label: string;
};
export type PartitionedEngagements = {
  engagements: CandidateEngagement[];
  upcomingEngagements: CandidateEngagement[];
  opportunities: CandidateEngagement[];
  submissions: CandidateEngagement[];
};

export enum EngagementsViewSections {
  upcomingEngagements = 'upcomingEngagements',
  opportunities = 'opportunities',
  submissions = 'submissions',
}

export type EngagementsViewSection = EngagementsViewSections;

type Engagement = CandidateEngagement | CandidateEngagementDetails;

type EngagementArg = { engagement: Engagement };
type SubmissionArg = { submission: Engagement };

const actionNeededStates = new Set([EngagementStates.Warmed]);

const countActionNeededInterviews = (engagement: Engagement): number =>
  engagement.interviews.filter(
    interview =>
      interview.interviewingState === EngagementStates.InterviewingUnaccepted
  ).length;

export const EngagementsUtil = {
  sortByCandidateStatusUpdatedAt(
    a: CandidateEngagement,
    b: CandidateEngagement
  ) {
    const timestampA = new Date(a.candidateStatusUpdatedAt).getTime();
    const timestampB = new Date(b.candidateStatusUpdatedAt).getTime();

    return timestampB - timestampA;
  },

  getStageCode: ({ engagement }: EngagementArg) => {
    return engagement.opportunity.stageCode?.toUpperCase() ?? null;
  },

  isStartDateAfterToday: ({ engagement }: EngagementArg) => {
    return DateUtil.isDateAfterDate(
      engagement.position?.startDate ?? new Date(),
      new Date()
    );
  },

  getSectionFromEngagement({
    engagement,
  }: EngagementArg): EngagementsViewSection {
    const stageCode = EngagementsUtil.getStageCode({ engagement });
    const isStartLater = EngagementsUtil.isStartDateAfterToday({ engagement });

    if (
      (stageCode === 'W' && isStartLater) ||
      (stageCode === '3' &&
        isStartLater &&
        engagement.onboardingEmailSentAt !== null)
    ) {
      return EngagementsViewSections.upcomingEngagements;
    } else if (engagement.candidateStatus === CandidateStatuses.Engaged) {
      return EngagementsViewSections.submissions;
    } else if (
      CurrentOpportunityPartitionStatuses.has(engagement.candidateStatus)
    ) {
      return EngagementsViewSections.opportunities;
    } else if (
      CurrentSubmissionPartitionStatuses.has(engagement.candidateStatus)
    ) {
      return EngagementsViewSections.submissions;
    }

    // Default fallback
    return EngagementsViewSections.submissions;
  },

  getBadgeConfig({ engagement }: EngagementArg): BadgeConfig[] {
    const actionNeeded: BadgeConfig = {
      badge: Badges.ActionNeeded,
      color: 'red',
      label: 'Action Needed',
    };
    const badges = engagement.hasMovedPastWarmedWithoutAnsweringWarmingQuestions
      ? [actionNeeded]
      : [];
    switch (engagement.engagementState) {
      case EngagementStates.New: {
        badges.push({
          badge: Badges.New,
          color: 'forest green',
          label: 'New',
        });
        break;
      }
      case EngagementStates.Warmed:
      case EngagementStates.InterviewingUnaccepted: {
        if (!badges.includes(actionNeeded)) {
          badges.push(actionNeeded);
        }
        break;
      }
      case EngagementStates.InterviewingClientUnacceptedTalentAccepted:
      case EngagementStates.InterviewingExpectedManual:
      case EngagementStates.InterviewingExpectedExternal: {
        badges.push({
          badge: Badges.InterviewExpected,
          color: 'forest green',
          label: 'Interview Expected',
        });
        break;
      }
      case EngagementStates.Interviewed: {
        badges.push({
          badge: Badges.WaitingOnClient,
          color: 'blue',
          label: 'Waiting On Client',
        });
        break;
      }
      case EngagementStates.InterviewingConfirmed: {
        const earliest = engagement.interviews[0];
        badges.push({
          badge: Badges.InterviewConfirmed,
          color: 'forest green',
          label: `Interview${earliest?.startTime ? ` - ${DateUtil.displayRelativeTime(earliest.startTime)}` : ''}`,
        });
        break;
      }
      case EngagementStates.InterviewingCancelled:
      case EngagementStates.InterviewingTalentDeclined: {
        badges.push({
          badge: Badges.InterviewCanceled,
          color: 'gray',
          label: 'Interview Canceled',
        });
        break;
      }
      default:
    }
    return badges;
  },

  sortCurrentEngagements({
    engagements,
  }: {
    engagements: CandidateEngagement[];
  }): CandidateEngagement[] {
    const badges = engagements.reduce<
      Record<CandidateEngagement['opportunity']['id'], BadgeConfig[]>
    >((acc, curr) => {
      acc[curr.opportunity.id] = EngagementsUtil.getBadgeConfig({
        engagement: curr,
      });
      return acc;
    }, {});
    return engagements.sort((a, b) => {
      const badgesA = badges[a.opportunity.id] ?? [];
      const badgesB = badges[b.opportunity.id] ?? [];

      if (
        badgesA.some(
          badgeConfig => badgeConfig.badge === Badges.ActionNeeded
        ) &&
        !badgesB.some(badgeConfig => badgeConfig.badge === Badges.ActionNeeded)
      ) {
        return -1;
      }
      if (
        !badgesA.some(
          badgeConfig => badgeConfig.badge === Badges.ActionNeeded
        ) &&
        badgesB.some(badgeConfig => badgeConfig.badge === Badges.ActionNeeded)
      ) {
        return 1;
      }
      if (badgesA.length > 0 && badgesB.length === 0) {
        return -1;
      }
      return badgesA.length === 0 && badgesB.length > 0
        ? 1
        : EngagementsUtil.sortByCandidateStatusUpdatedAt(a, b);
    });
  },

  partitionCurrentEngagements({
    engagements,
  }: {
    engagements: CandidateEngagement[];
  }): PartitionedEngagements {
    return EngagementsUtil.sortCurrentEngagements({
      engagements,
    }).reduce<PartitionedEngagements>(
      (acc, curr) => {
        const stageCode = EngagementsUtil.getStageCode({ engagement: curr });
        const isStartLater = EngagementsUtil.isStartDateAfterToday({
          engagement: curr,
        });

        if (
          EngagementsUtil.isActiveEngagement({ engagement: curr }) ||
          (stageCode === '3' &&
            !isStartLater &&
            curr.onboardingEmailSentAt !== null &&
            curr.candidateStatus === CandidateStatuses.Selected)
        ) {
          acc.engagements.push(curr);
        } else {
          const section = EngagementsUtil.getSectionFromEngagement({
            engagement: curr,
          });
          acc[section].push(curr);
        }

        return acc;
      },
      {
        engagements: [] as CandidateEngagement[],
        upcomingEngagements: [] as CandidateEngagement[],
        opportunities: [] as CandidateEngagement[],
        submissions: [] as CandidateEngagement[],
      }
    );
  },

  getCurrentEngagementsCounts({
    partitionedEngagements,
  }: {
    partitionedEngagements: PartitionedEngagements;
  }): Record<string, number> {
    return Object.fromEntries(
      Object.entries(partitionedEngagements).map(([key, value]) => [
        key,
        value.reduce(
          (count, engagement) =>
            count +
            (engagement.hasMovedPastWarmedWithoutAnsweringWarmingQuestions
              ? 1
              : 0) +
            (engagement.engagementState !== null &&
            actionNeededStates.has(engagement.engagementState)
              ? 1
              : 0) +
            countActionNeededInterviews(engagement),
          0
        ),
      ])
    );
  },

  filterCurrentEngagements({
    opportunities,
  }: {
    opportunities: OpportunityCandidate[];
  }): OpportunityCandidate[] {
    const currentEngagements = opportunities.filter(opp => {
      const { candidateStatus, position } = opp;
      const isStartDateTodayOrInPast =
        position?.startDate &&
        DateUtil.isDateBeforeDate(position.startDate, undefined, true);

      return (
        candidateStatus === CandidateStatuses.Engaged &&
        isStartDateTodayOrInPast
      );
    });

    // sorted by confirmed end date and billing hours per week descending
    return currentEngagements.sort((a, b) => {
      const aConfirmedEndDate = a.position?.endDateStatus === 'Confirmed';
      const bConfirmedEndDate = b.position?.endDateStatus === 'Confirmed';

      const aEndDate = a.position?.endDate
        ? new Date(a.position.endDate)
        : null;
      const bEndDate = b.position?.endDate
        ? new Date(b.position.endDate)
        : null;

      const aBillingHoursPerWeek = a.position?.billingHoursPerWeek ?? 0;
      const bBillingHoursPerWeek = b.position?.billingHoursPerWeek ?? 0;

      // Prioritize confirmed end dates
      if (aConfirmedEndDate && !bConfirmedEndDate) return -1;
      if (!aConfirmedEndDate && bConfirmedEndDate) return 1;

      // Handle null end dates: Consider engagements with null end dates as greater
      if (!aEndDate && !bEndDate) {
        return bBillingHoursPerWeek - aBillingHoursPerWeek;
      } else if (!aEndDate) {
        return 1;
      } else if (!bEndDate) {
        return -1;
      }

      // Sort by end date
      if (aEndDate.getTime() !== bEndDate.getTime()) {
        return aEndDate.getTime() - bEndDate.getTime();
      }

      // If end dates are the same, sort by billing hours per week
      return bBillingHoursPerWeek - aBillingHoursPerWeek;
    });
  },

  filterFutureEngagements({
    opportunities,
  }: {
    opportunities: OpportunityCandidate[];
  }): OpportunityCandidate[] {
    const futureEngagements = opportunities.filter(opp => {
      const { candidateStatus, position } = opp;
      const isStartDateInFuture =
        position?.startDate && DateUtil.isDateAfterDate(position.startDate);

      return (
        candidateStatus === CandidateStatuses.Engaged &&
        (isStartDateInFuture || !position?.startDate)
      );
    });

    return futureEngagements.sort((a, b) => {
      const aStartDate = a.position?.startDate
        ? new Date(a.position.startDate)
        : null;
      const bStartDate = b.position?.startDate
        ? new Date(b.position.startDate)
        : null;

      if (!aStartDate && !bStartDate) return 0;
      if (!aStartDate) return 1;
      if (!bStartDate) return -1;

      if (aStartDate.getTime() < bStartDate.getTime()) return -1;
      return aStartDate.getTime() > bStartDate.getTime() ? 1 : 0;
    });
  },

  isActiveEngagement({ engagement }: EngagementArg) {
    return !!(
      engagement.candidateStatus === CandidateStatuses.Engaged &&
      engagement.position?.startDate &&
      DateUtil.isDateBeforeDate(engagement.position.startDate, new Date(), true)
    );
  },

  engagementLength({ engagement }: EngagementArg) {
    const sub = {
      label: 'Estimated Length',
      name: 'submissions',
      value:
        PositionUtil.getEngagementLengthGrouped(engagement.position) ?? '--',
    };

    if (engagement.candidateStatus === CandidateStatuses.Engaged) {
      if (
        engagement.position?.startDate &&
        DateUtil.isDateBeforeDate(
          engagement.position.startDate,
          new Date(),
          true
        )
      ) {
        return {
          label: 'Engagement Length',
          name: 'engagements',
          value:
            PositionUtil.getEngagementLengthSingle(engagement.position) ?? '--',
        };
      }
      return sub;
    } else if (
      CurrentOpportunityPartitionStatuses.has(engagement.candidateStatus)
    ) {
      return {
        label: 'Estimated Length',
        name: 'opportunities',
        value:
          PositionUtil.getEngagementLengthGrouped(engagement.position) ?? '--',
      };
    } else if (
      CurrentSubmissionPartitionStatuses.has(engagement.candidateStatus)
    ) {
      return sub;
    }

    return sub;
  },

  getWeeklyEngagementText({ submission }: SubmissionArg) {
    return !submission.position ||
      !('billingHoursPerWeek' in submission.position) ||
      !submission.position.billingHoursPerWeek
      ? '--'
      : `${submission.position.billingHoursPerWeek} hours`;
  },
};
